import { createSelector } from 'reselect';
import * as qs from 'querystring';
import { selectors as sessionSelectors } from '@mulesoft/exchange-ui-portals-store/lib/domains/session';
import { selectors as commonSelectors } from '~/domains/common';
import { selectors as featureFlagsSelectors } from '~/domains/featureFlags';
import { selectors as localStorageSelectors } from '~/domains/localStorage';
import {
  ANG_SEARCH_ENABLED_FLAG,
  ANG_SIDEBAR_CONSISTENCY_ENABLED,
  GLOBAL_ANG_SEARCH_ENABLED,
  PORTAL_RE_ARCHITECTURE_ENABLED,
  ANG_DEFAULT_ENABLED
} from '~/domains/featureFlags/keys';
import ANGFilters from '~/utils/ang/ANGFilters';
import * as objectsUtils from '~/utils/objects';
import getConfig from './config';

const emptyArray = [];
const daysSinceLastShownThreshold = 1;
const oneDayToMiliseconds = 1000 * 60 * 60 * 24;

export const facets = (state) => state.search.facets;
export const facetsErrorDate = (state) => state.search?.fetchingFacetsErrorDate;
export const isANGAvailable = (state) => !state.search?.fetchingFacetsErrorDate;
export const facetsHasBeenRefreshed = (state) =>
  state.search.facetsHasBeenRefreshed;

export const shouldRefreshFacets = createSelector(
  commonSelectors.homeQuery,
  (state, query) => query,
  (state) => state.search.shouldRefreshFacets,
  ($homeQuery, $query, $shouldRefreshFacets) => {
    if ($shouldRefreshFacets) {
      return true;
    }

    const stringifiedNewQuery = qs.stringify($query);
    const stringifiedHomeQuery = qs.stringify($homeQuery);

    if (stringifiedHomeQuery !== stringifiedNewQuery) {
      return true;
    }

    return false;
  }
);

const isANGSearchFlagEnabled = (state) =>
  !commonSelectors.isStudio(state) &&
  featureFlagsSelectors.featureFlag(state, ANG_SEARCH_ENABLED_FLAG);

const isGlobalANGSearchFlagEnabled = (state) =>
  featureFlagsSelectors.featureFlag(state, GLOBAL_ANG_SEARCH_ENABLED);

const isANGDefaultFlagEnabled = (state) =>
  featureFlagsSelectors.featureFlag(state, ANG_DEFAULT_ENABLED);

const isANGSearchOptInEnabled = createSelector(
  commonSelectors.profile,
  ($profile) =>
    !!$profile &&
    objectsUtils.getProp($profile, 'properties.exchange.isANGSearchEnabled') ===
      'true'
);

const isANGSearchOptOutEnabled = createSelector(
  commonSelectors.profile,
  ($profile) =>
    !!$profile &&
    objectsUtils.getProp(
      $profile,
      'properties.exchange.isANGSearchDisabled'
    ) === 'true'
);

export const isANGSearchOptedIn = createSelector(
  isANGDefaultFlagEnabled,
  isANGSearchOptInEnabled,
  isANGSearchOptOutEnabled,
  ($isANGDefaultFlagEnabled, $isOptInEnabled, $isOptOutEnabled) =>
    $isANGDefaultFlagEnabled ? !$isOptOutEnabled : $isOptInEnabled
);

export const isANGSearchEnabled = createSelector(
  isANGAvailable,
  isANGSearchFlagEnabled,
  isGlobalANGSearchFlagEnabled,
  isANGSearchOptedIn,
  commonSelectors.profile,
  (
    $isANGAvailable,
    $isEnabled,
    $isGlobalEnabled,
    $isANGSearchOptedIn,
    $profile
  ) =>
    $isANGAvailable &&
    ($profile ? $isEnabled && $isANGSearchOptedIn : $isGlobalEnabled)
);

export const isANGQuery = createSelector(
  isANGSearchEnabled,
  (state) =>
    featureFlagsSelectors.featureFlag(state, ANG_SIDEBAR_CONSISTENCY_ENABLED),
  (_, query) => query,
  ($isANGSearchEnabled, $isANGSidebarConsistencyEnabled, $query) => {
    const hasANGQuery = ANGFilters.hasQuery($query);

    return !!(
      $isANGSearchEnabled &&
      (hasANGQuery || $isANGSidebarConsistencyEnabled)
    );
  }
);

export const isANGSearchOptInVisible = createSelector(
  isANGAvailable,
  isANGSearchFlagEnabled,
  isANGSearchOptedIn,
  commonSelectors.profile,
  ($isANGAvailable, $isEnabled, $isANGSearchOptedIn, $profile) =>
    $isANGAvailable && $isEnabled && !$isANGSearchOptedIn && !!$profile
);

const OPT_IN_BANNER_DISABLED_KEY = 'isANGSearchOptInBannerDisabled';

export const isANGSearchOptInBannerVisible = createSelector(
  isANGSearchOptInVisible,
  isANGDefaultFlagEnabled,
  (state) =>
    localStorageSelectors.localStorageItem(state, OPT_IN_BANNER_DISABLED_KEY),
  sessionSelectors.isPublicPortal,
  (
    $isANGSearchOptInVisible,
    $isANGDefaultFlagEnabled,
    $isDisabled,
    $isPublicPortal
  ) =>
    $isANGSearchOptInVisible &&
    !$isANGDefaultFlagEnabled &&
    !$isDisabled &&
    !$isPublicPortal
);

export const isANGSearchOptOutVisible = createSelector(
  isANGAvailable,
  isANGSearchFlagEnabled,
  isANGSearchOptedIn,
  ($isANGAvailable, $isEnabled, $isANGSearchOptedIn) =>
    $isANGAvailable && $isEnabled && $isANGSearchOptedIn
);

const OPT_OUT_BANNER_DISABLED_KEY = 'isANGSearchOptOutBannerDisabled';

export const isANGSearchOptOutBannerVisible = createSelector(
  isANGSearchOptOutVisible,
  isANGDefaultFlagEnabled,
  (state) =>
    localStorageSelectors.localStorageItem(state, OPT_OUT_BANNER_DISABLED_KEY),
  sessionSelectors.isPublicPortal,
  (
    $isANGSearchOptOutVisible,
    $isANGDefaultFlagEnabled,
    $isDisabled,
    $isPublicPortal
  ) =>
    $isANGSearchOptOutVisible &&
    !$isANGDefaultFlagEnabled &&
    !$isDisabled &&
    !$isPublicPortal
);

const TEMP_DISABLED_BANNER_LAST_TIME_SHOWN_KEY =
  'lastTimeANGTemporarilyDisabledShown';

export const isANGTemporarilyDisabledBannerVisible = createSelector(
  facetsErrorDate,
  isANGSearchFlagEnabled,
  isANGSearchOptInEnabled,
  (state) =>
    localStorageSelectors.localStorageItem(
      state,
      TEMP_DISABLED_BANNER_LAST_TIME_SHOWN_KEY
    ),
  commonSelectors.isStudio,
  sessionSelectors.isPublicPortal,
  (
    $facetsErrorDate,
    $isANGSearchFlagEnabled,
    $isANGSearchOptInEnabled,
    $lastTimeShown,
    $isStudio,
    $isPublicPortal
  ) => {
    const $isANGAvailable = !$facetsErrorDate;
    const isRecentFacetsError =
      !!$facetsErrorDate &&
      !!$lastTimeShown &&
      new Date($lastTimeShown) - new Date($facetsErrorDate) > 0;
    const hasBeenRecentlyShown =
      // Diff between today's date and last time shown converted to days
      Math.floor(
        (new Date() - new Date($lastTimeShown || null)) / oneDayToMiliseconds
      ) < daysSinceLastShownThreshold;

    if ($isStudio || $isPublicPortal || !$isANGSearchOptInEnabled) {
      return false;
    }

    if ($isANGSearchFlagEnabled && !$isANGAvailable && !isRecentFacetsError) {
      return true;
    }

    return !$isANGSearchFlagEnabled && !hasBeenRecentlyShown;
  }
);

export const isANGPaginationEnabled = createSelector(
  isANGSearchEnabled,
  (state) =>
    featureFlagsSelectors.featureFlag(state, ANG_SIDEBAR_CONSISTENCY_ENABLED),
  ($isANGSearchEnabled, $isANGSidebarConsistencyEnabled) =>
    $isANGSearchEnabled && $isANGSidebarConsistencyEnabled
);

export const categoriesFilter = (state) => state.search.facets?.categories;

export const organizations = (state) => state.search.facets?.organizations;

export const rootOrganizations = (state) => state.search?.rootOrganizations;

export const facetsResponseTime = (state) => state.search.facets?.responseTime;

export const organizationsFilter = createSelector(
  facets,
  commonSelectors.profile,
  ($facets, $profile) => {
    if (!$facets || !$facets.organizations) {
      return emptyArray;
    }

    return $facets.organizations
      .map((organization) => {
        const isExternal =
          !$profile ||
          $profile.memberOfOrganizations.every(
            (orgMember) => orgMember.id !== organization.id
          );

        return {
          ...organization,
          group: isExternal ? 'External' : $profile.organization.name,
          isExternal
        };
      })
      .sort((orgA, orgB) => {
        if (orgA.isExternal === orgB.isExternal) {
          return orgA.name.localeCompare(orgB.name);
        }

        if (orgA.isExternal) {
          return 1;
        }

        return -1;
      });
  }
);

export const typesFilter = createSelector(
  facets,
  (state, params) => params.assetTypesConfiguration,
  ($facets, $typesList) =>
    $facets?.types?.reduce((accum, current) => {
      const typeKey = Object.keys($typesList).find(
        ($typeKey) => $typesList[$typeKey].angType === current
      );

      if (typeKey && $typesList[typeKey].filter) {
        return [...accum, typeKey];
      }

      return accum;
    }, [])
);

export const angTypesFilter = (state) => state.search.facets?.types;

export const tagsFilter = (state) => state.search.facets?.tags;

export const statusesFilter = (state) => state.search.facets?.status;

export const angConfig = createSelector(
  commonSelectors.context,
  categoriesFilter,
  organizationsFilter,
  typesFilter,
  tagsFilter,
  statusesFilter,
  featureFlagsSelectors.featureFlags,
  (state) =>
    featureFlagsSelectors.featureFlag(state, ANG_SIDEBAR_CONSISTENCY_ENABLED),
  (state) =>
    featureFlagsSelectors.featureFlag(state, PORTAL_RE_ARCHITECTURE_ENABLED),
  (state) => commonSelectors.profile(state),
  facetsHasBeenRefreshed,
  (state, params) => params.sort,
  (state, params) => params.show,
  (state, params) => params.search,
  (state, params) => params.updateFilter,
  (state, params) => params.boostConfiguration,
  (state, params) => params.assetTypesConfiguration,
  (state, params) => params.includeEmptyFilters,
  (
    $context,
    $categories,
    $organizations,
    $types,
    $tags,
    $statuses,
    $featureFlags,
    $isANGSidebarConsistencyEnabled,
    $isPortalReArchitectureEnabled,
    $profile,
    $facetsHasBeenRefreshed,
    $sort,
    $show,
    $search,
    $updateFilter,
    $boostConfiguration,
    $typesConfiguration,
    $includeEmptyFilters
  ) =>
    getConfig({
      context: $context,
      assetTypesConfiguration: $typesConfiguration,
      categories: $categories,
      types: $types,
      featureFlags: $featureFlags,
      isANGSidebarConsistencyEnabled: $isANGSidebarConsistencyEnabled,
      isPortalReArchitectureEnabled: $isPortalReArchitectureEnabled,
      isAuthenticated: !!$profile,
      organizations: $organizations,
      tags: $tags,
      statuses: $statuses,
      sort: $sort,
      show: $show,
      search: $search,
      updateFilter: $updateFilter,
      facetsHasBeenRefreshed: $facetsHasBeenRefreshed,
      boostConfiguration: $boostConfiguration,
      includeEmptyFilters: $includeEmptyFilters
    })
);
